<template>
	<div class="badges" v-if="show">
		<template v-if="energyAttr?.length && types.includes('energy')">
			<span class="energy" v-for="energy in energyAttr" :key="energy.id">
				<BaseUiImage v-if="energy.image_upload_path" loading="lazy" :src="energy.image_upload_path" default="/images/no-image.jpg" width="80" height="25" :title="energy.title" :alt="energy.title" />
				<span v-else>{{ energy.title }}</span>
			</span>
		</template>
		
		<div v-if="priceSaved && types.includes('discount')" class="badge badge-discount red">-<BaseUtilsFormatCurrency :price="priceSaved" /></div>
		
		<div v-if="item.element_state_code && conditionLabel && types.includes('condition')" class="badge condition-badge yellow" :class="{'nolink': !conditionLink}" @click="conditionLink && labels.get('flyout_content_condition_' + item.element_state_code.toLowerCase().replace('/', '')) && modal.open('flyout', {header: true, title: conditionLabel, content: labels.get('flyout_content_condition_' + item.element_state_code.toLowerCase().replace('/', ''))})">
			<span v-html="conditionLabel"></span>
		</div>

		<div v-if="item.selected_price == 'uau' && types.includes('uau')" class="badge badge-uau orange"><BaseCmsLabel code='uau_badge_title' /></div>
		<div v-if="item.selected_price == 'uaump' && types.includes('uaump')" class="badge badge-uaump"><BaseCmsLabel code='uaump_badge_title' /></div>

		<template v-if="imageBadges?.length && types.includes('image')">
			<div v-for="badge in imageBadges" :key="badge.code" class="image-badge">
				<BaseUiImage loading="lazy" :src="badge.badge_image_upload_path" default="/images/no-image-40.webp" width="40" height="40" :alt="badge.title" />
			</div>
		</template>
		
		<template v-if="textBadges?.length && types.includes('text')">
			<template v-for="badge in textBadges" :key="badge.code">
				<component v-if="badge.label_title" :is="badge.badge_url ? NuxtLink : 'div'" :to="badge.badge_url ? badge.badge_url : null" :class="['badge', 'cp-badge-' + badge.code, {'link': badge.badge_url}]">
					{{ badge.label_title }}
					<template v-if="props.tooltips">
						<LazyUiTooltip v-if="badge.label_title_hover && badge.label_title_hover != '/'" class="arrow-down">
							{{ badge.label_title_hover }}
						</LazyUiTooltip>
						<LazyUiTooltip v-else-if="!badge.label_title_hover && item.date_available && badge.label_title_hover != '/' && badge.category == 1" class="arrow-down">
							{{ labels.get('izid') }} <strong>{{ item.date_available }}</strong>
						</LazyUiTooltip>
						<LazyUiTooltip v-else-if="!badge.label_title_hover && (badge.coupon_code || badge.coupon_discount_percent) && badge.label_title_hover != '/' && badge.category == 2">
							<template v-if="badge.coupon_code">{{ badge.coupon_code }}</template>
							<template v-if="badge.coupon_discount_percent">
								(
								<template v-if="item.discount_percent_custom >= 2 && item.price_custom < item.basic_price_custom">
									{{ labels.get('prihrani_se') }}
								</template>
								<template v-else>
									{{ labels.get('prihrani') }}
								</template>
								<strong>{{ badge.coupon_discount_percent }} %</strong>
								)
							</template>
						</LazyUiTooltip>
					</template>
				</component>
				<div v-if="badge.category == 4 && types.includes('new')" class="badge badge-new"><BaseCmsLabel code='new_badge' /></div>
			</template>
		</template>
	</div>
</template>

<script setup>
	import {NuxtLink} from '#components';
	const labels = useLabels();
	const props = defineProps({
		item: {
			type: Object,
			required: true,
		},
		tooltips: {
			type: Boolean,
			default: false,
		},
		types: {
			type: Array,
			default: () => ['discount', 'new', 'uau', 'uaump', 'condition'],
		},
		conditionLink: {
			type: Boolean,
			default: true,
		},
	});
	const modal = useModal();
	const item = toRef(props, 'item');
	const types = toRef(props, 'types');
	const {textBadges, imageBadges, priceSaved, energyAttr} = useProductData(item);

	const show = computed(() => {
		return types.value.some(type => {
			return type == 'discount' && priceSaved.value
				|| type == 'new' && textBadges.value.find(badge => badge.category == 4)
				|| type == 'uau' && item.value.selected_price == 'uau'
				|| type == 'uaump' && item.value.selected_price == 'uaump'
				|| type == 'condition' && item.value.element_state_code && item.value.element_state_code != 'n'
				|| type == 'energy' && energyAttr.value
				|| type == 'image' && imageBadges.value.length
				|| type == 'text' && textBadges.value.length;
		});
	});

	const conditionLabel = computed(() => {
		const lbls = labels.get('product_condition');
		const state = props.item.element_state_code;
		if (!lbls || !state) return '';

		const states = lbls.split('\n');
		const stateEntry = states.find(entry => entry.startsWith(state));
		return stateEntry ? stateEntry.split('=')[1] : '';
	});
</script>

<style scoped lang="less">
	.badges{display: flex; flex-wrap: wrap; gap: 6px; align-items: center;}
	.badge{
		display: inline-flex; align-items: center; justify-content: center; min-height: 22px; padding: 2px 8px; background: var(--blueDark); border-radius: 4px; font-size: 12px; font-weight: 600; color: var(--white); white-space: nowrap; position: relative; z-index: 11; cursor: default;
		&.link{cursor: pointer;}
		&.red{background: var(--errorColor);}
		&.blue{background: var(--blue);}
		&.orange{background: var(--orange);}
		&.yellow{background: #CDD700; color: var(--blueDark);}
		&.nolink{cursor: default;}
		&.badge-uaump{background: var(--uaump); text-transform: uppercase;}
		:deep(.tooltip){
			top: calc(-100% - 15px); display: none;
			@media (max-width: @m){display: none!important;}
		}
		&:hover{
			:deep(.tooltip){display: block;}
		}
	}
	.condition-badge{cursor: pointer;}
	.energy{
		height: 20px; font-size: 12px; position: relative; z-index: 11; width: 100%; flex-shrink: 0; flex-grow: 0;
		:deep(img){display: block; width: auto; height: auto; max-width: 100%; max-height: 100%;}
	}
</style>